/**
 * PostMessage validation utilities
 * Security is primarily enforced by CSP frame-ancestors (server-side).
 * Client-side validation ensures messages come from the parent window only.
 */

import * as Sentry from "@sentry/react";

/**
 * Validate incoming postMessage event
 * Only accepts messages from the parent window (our embedder)
 */
export function validateMessageOrigin(event: MessageEvent): boolean {
  // In test mode, skip origin validation to allow Playwright CT tests
  if (import.meta.env.VITE_PLAYWRIGHT_TEST === "true") {
    return true;
  }

  // If we're not in an iframe, reject all postMessages
  if (window.self === window.top) {
    return false;
  }

  // Accept messages from parent or top. CSP frame-ancestors already enforces
  // which origins can embed us.
  return event.source === window.parent || event.source === window.top;
}

/**
 * Secure postMessage sender to parent window.
 * Callers should supply the captured parent origin as `targetOrigin` whenever
 * it is known so that outbound messages carrying sensitive data (transaction
 * payloads, wallet state) are never delivered to a wrong-origin frame.
 * Falls back to "*" only for the initial handshake before the parent origin
 * has been established.
 */
export function sendSecurePostMessage(
  targetWindow: Window,
  message: unknown,
  targetOrigin = "*",
): void {
  targetWindow.postMessage(message, targetOrigin);
}

/**
 * Higher-order function to create validated message listeners
 */
export function createSecureMessageListener<T = unknown>(
  handler: (data: T, event: MessageEvent) => void,
  options: {
    expectedTypes?: string[];
    validateData?: (data: unknown) => data is T;
  } = {},
): (event: MessageEvent) => void {
  return (event: MessageEvent) => {
    // Validate source - only accept messages from parent window
    if (!validateMessageOrigin(event)) {
      // Skip logging for browser extensions (they send messages from same window)
      const isBrowserExtension = event.source === window.self;

      if (!isBrowserExtension) {
        console.warn("Rejected message from non-parent source", {
          origin: event.origin,
          messageType: event.data?.type || "unknown",
        });

        // Report unexpected postMessage source to Sentry (excluding browser extensions)
        Sentry.withScope((scope) => {
          scope.setTag("security_event", "unexpected_postmessage_source");
          scope.setTag("origin", event.origin);
          scope.setLevel("warning");
          scope.setContext("postMessage", {
            origin: event.origin,
            messageType: event.data?.type || "unknown",
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            location: window.location.href,
          });

          Sentry.captureMessage(`PostMessage from non-parent source: ${event.origin}`, "warning");
        });
      }

      return;
    }

    // Validate message structure
    if (!event.data || typeof event.data !== "object") {
      console.warn("Rejected malformed message:", event.data);
      return;
    }

    // Validate message type if specified
    if (options.expectedTypes && !options.expectedTypes.includes(event.data.type)) {
      console.warn("Rejected message with unexpected type:", event.data.type);
      return;
    }

    // Custom data validation if provided
    if (options.validateData && !options.validateData(event.data)) {
      console.warn("Rejected message that failed data validation:", event.data);
      return;
    }

    // Call the handler with validated data
    handler(event.data, event);
  };
}
